import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CheckIcon,
} from "@heroicons/react/solid";

/**
 * AccordionDropdown
 * A dropdown select component supporting:
 * 1. Flat options: [{ label, value }]
 * 2. Flat options with "group": [{ label, value, group: "Recent" }]
 * 3. Pre-nested group options: [{ label, options: [{ label, value }] }]
 *
 * @param {Array} options - Array of options
 * @param {Object} value - Currently selected option { label, value, name }
 * @param {Function} onChange - Callback function called with selected option
 * @param {string} placeholder - Placeholder text when nothing selected
 * @param {string} className - Optional container className
 * @param {boolean} disabled - Disabled state
 */
const AccordionDropdown = ({
  options = [],
  value = null,
  onChange = () => {},
  placeholder = "Select an option...",
  className = "",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const dropdownRef = useRef(null);

  // Normalize input options: convert flat array with 'group' property into structured groups
  const normalizedStructure = useMemo(() => {
    if (!Array.isArray(options) || options.length === 0) {
      return { isGrouped: false, items: [] };
    }

    // Case 1: Already pre-nested groups
    const hasNestedGroups = options.some((opt) => Array.isArray(opt?.options));
    if (hasNestedGroups) {
      return { isGrouped: true, items: options };
    }

    // Case 2: Flat options containing the 'group' property
    const hasGroupProperty = options.some((opt) => opt && opt.group);
    if (!hasGroupProperty) {
      // Case 3: Standard flat options without groups
      return { isGrouped: false, items: options };
    }

    // Group items by their 'group' property preserving first-seen order
    const groupMap = new Map();
    const ungrouped = [];

    options.forEach((item) => {
      if (item && item.group) {
        if (!groupMap.has(item.group)) {
          groupMap.set(item.group, []);
        }
        groupMap.get(item.group).push(item);
      } else if (item) {
        ungrouped.push(item);
      }
    });

    const structuredItems = [];
    groupMap.forEach((groupOptions, groupName) => {
      structuredItems.push({
        label: groupName,
        group: groupName,
        options: groupOptions,
      });
    });

    // Append any items that had no group as flat items
    ungrouped.forEach((item) => structuredItems.push(item));

    return { isGrouped: true, items: structuredItems };
  }, [options]);

  const { isGrouped, items: renderedItems } = normalizedStructure;

  // Auto-expand group of active item on mount or value change
  useEffect(() => {
    if (value && isGrouped) {
      const targetVal =
        typeof value === "object" ? (value.value ?? value.name) : value;
      const groupIdx = renderedItems.findIndex(
        (group) =>
          Array.isArray(group?.options) &&
          group.options.some((item) => (item.value ?? item.name) === targetVal),
      );
      if (groupIdx !== -1) {
        setExpandedGroups({ [groupIdx]: true });
      }
    }
  }, [value, isGrouped, renderedItems]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const toggleGroup = (index, e) => {
    e.stopPropagation();
    setExpandedGroups((prev) => ({
      [index]: !prev[index],
    }));
  };

  const handleSelectOption = (item) => {
    onChange(item);
    setIsOpen(false);
  };

  const getSelectedLabel = () => {
    if (!value) return placeholder;
    return value.label || value.name || value.value || placeholder;
  };

  const isItemSelected = (item) => {
    if (!value || !item) return false;
    const selectedVal = value.value ?? value.name;
    const itemVal = item.value ?? item.name;
    return selectedVal === itemVal;
  };

  return (
    <div
      ref={dropdownRef}
      className={`relative inline-block text-left ${className}`}
      style={{ minWidth: "160px" }}
    >
      {/* Trigger Button - styled like custom-select-option__control */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((prev) => !prev)}
        className="custom-select-option__control min-h-[38px] w-full flex items-center justify-between gap-2.5 px-3 py-1 cursor-pointer select-none outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        style={{
          borderRadius: "20px",
          background: "#f7f8fc",
          border: "1px solid #d3d4d5",
          boxShadow: "none",
        }}
      >
        <div className="custom-select-option__value-container flex items-center flex-1 truncate">
          <span className="custom-select-option-value truncate text-left">
            {getSelectedLabel()}
          </span>
        </div>
        <div className="flex items-center text-[#7f8c8d] pr-0.5">
          <ChevronDownIcon
            className={`w-4 h-4 transition-transform duration-200 ${
              isOpen ? "transform rotate-180" : ""
            }`}
          />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute right-0 mt-1.5 w-60 max-h-80 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 text-sm scrollbar-thin scrollbar-thumb-gray-300"
          style={{
            boxShadow:
              "0 0 0 1px hsla(0, 0%, 0%, 0.1), 0 4px 11px hsla(0, 0%, 0%, 0.1)",
            borderRadius: "8px",
          }}
        >
          {renderedItems.length === 0 && (
            <div className="custom-select-option text-gray-400 text-center py-2">
              No options
            </div>
          )}

          {renderedItems.map((optionOrGroup, idx) => {
            if (Array.isArray(optionOrGroup?.options)) {
              // Grouped item (Accordion)
              const isExpanded = !!expandedGroups[idx];
              const groupLabel =
                optionOrGroup.label ||
                optionOrGroup.group ||
                `Group ${idx + 1}`;

              return (
                <div
                  key={`group-${idx}`}
                  className="border-b border-gray-100 last:border-b-0"
                >
                  {/* Group Header */}
                  <div
                    onClick={(e) => toggleGroup(idx, e)}
                    className="flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/75 hover:bg-gray-100/90 cursor-pointer select-none transition-colors"
                  >
                    <span>{groupLabel}</span>
                    <span className="text-gray-400">
                      {isExpanded ? (
                        <ChevronDownIcon className="w-3.5 h-3.5" />
                      ) : (
                        <ChevronRightIcon className="w-3.5 h-3.5" />
                      )}
                    </span>
                  </div>

                  {/* Group Children */}
                  {isExpanded && (
                    <div className="py-0.5 bg-white">
                      {optionOrGroup.options.map((item, itemIdx) => {
                        const selected = isItemSelected(item);
                        return (
                          <div
                            key={`item-${idx}-${itemIdx}`}
                            onClick={() => handleSelectOption(item)}
                            className={`custom-select-option flex items-center justify-between pl-6 pr-3 transition-colors ${
                              selected
                                ? "bg-[#e8f0fe] font-semibold !text-[#111111]"
                                : "hover:bg-[#f2f4f8] text-[#464646]"
                            }`}
                          >
                            <span className="truncate">
                              {item.label || item.name || item.value}
                            </span>
                            {selected && (
                              <CheckIcon className="w-4 h-4 text-primary" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // Flat item
            const selected = isItemSelected(optionOrGroup);
            return (
              <div
                key={`flat-${idx}`}
                onClick={() => handleSelectOption(optionOrGroup)}
                className={`custom-select-option flex items-center justify-between px-3 transition-colors ${
                  selected
                    ? "bg-[#e8f0fe] font-semibold !text-[#111111]"
                    : "hover:bg-[#f2f4f8] text-[#464646]"
                }`}
              >
                <span className="truncate">
                  {optionOrGroup.label ||
                    optionOrGroup.name ||
                    optionOrGroup.value}
                </span>
                {selected && <CheckIcon className="w-4 h-4 text-primary" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AccordionDropdown;
